<template>
  <b-list-group-item
    :button="!showEditAppointmentForm"
    @click="showEditAppointmentForm = true"
  >
    <div
      v-if="!showEditAppointmentForm"
      class="d-flex justify-content-between align-items-center"
    >
      <span>
        <gb-flag
          :code="appointment.contact.nationalityCountryCode"
          size="small"
          class="mr-2"
          v-if="
            appointment.contact &&
            appointment.contact.nationalityCountryCode &&
            !appointment.contact.isCompany
          "
        />
        <span
          style="width: 25px"
          class="mr-2 d-inline-block text-center"
          v-if="appointment.contact.isCompany"
        >
          <i class="fas fa-user-tie"></i>
        </span>
        {{ appointment.contact.name | decode }}
      </span>
      <b-badge v-if="appointment.isCurrent" variant="info" pill
        >Current</b-badge
      >
      <b-badge v-if="!appointment.isCurrent" variant="default" pill
        >Resigned</b-badge
      >
    </div>
    <div v-else>
      <contact-edit-form
        :account-id="accountId"
        :modal-id="`edit-contact-${appointment.id}-${appointment.contact.id}`"
        :contact="appointment.contact"
        @update-contact="contactUpdated"
      ></contact-edit-form>
      <b-row>
        <b-col sm="6">
          <label class="form-control-label">Contact name</label>
          <br />
          <b-btn
            v-b-modal="
              `edit-contact-${appointment.id}-${appointment.contact.id}`
            "
            title="Edit contact"
            v-b-tooltip.hover
          >
            <i class="fa fa-user mr-2 text-muted"></i>
            <span v-html="appointment.contact.name"></span>
          </b-btn>
        </b-col>
        <b-col sm="6">
          <b-row>
            <b-col sm="8">
              <base-input
                label="Appointment Date"
                name="Appointment Date"
                type="date"
                v-model="appointment.startDate"
              ></base-input>
            </b-col>
            <b-col sm="4">
              <label class="form-control-label">Resigned?</label>
              <br />
              <base-switch class="mr-1" v-model="hasResigned"></base-switch>
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="8">
              <base-input
                v-if="hasResigned"
                label="Resignation Date"
                name="Resignation Date"
                type="date"
                v-model="appointment.endDate"
              ></base-input>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col cols="6">
          <b-btn variant="link" @click="deleteAppointment">
            <i class="fa fa-trash mr-2"></i>
            Delete appointment
          </b-btn>
        </b-col>

        <b-col cols="6" class="text-right">
          <b-btn @click.stop="cancel">
            <i class="fa fa-times mr-2 text-muted"></i>
            Cancel
          </b-btn>

          <b-btn @click="update" variant="success">
            <span v-if="!updating">
              <i class="fa fa-check mr-2"></i>
              Save
            </span>
            <span v-else>
              <i class="fa fa-spin fa-spinner mr-2"></i>
              Please wait&hellip;
            </span>
          </b-btn>
        </b-col>
      </b-row>
    </div>
  </b-list-group-item>
</template>

<script>
import { get } from "dot-prop";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import moment from "moment";
import ContactEditForm from "../Contacts/ContactEditForm";

export default {
  name: "appointment-list-group-item",
  components: {
    ContactEditForm,
  },
  props: {
    accountId: {
      type: String,
      description: "Account ID",
      required: true,
    },
    companyId: {
      type: String,
      description: "Company ID",
      required: true,
    },
    appointment: {
      type: Object,
      description: "Appointment",
      required: true,
    },
    classes: {
      type: String,
      description: "Classes", // TODO: use this in template
      required: false,
    },
  },
  data() {
    return {
      showEditAppointmentForm: false,
      updating: false,
      errorMessage: null,
    };
  },
  computed: {
    hasResigned: {
      get() {
        return this.appointment.endDate !== null;
      },
      set(val) {
        if (val === true) {
          this.appointment.endDate = moment().format("YYYY-MM-DD");
        } else {
          this.appointment.endDate = null;
        }
      },
    },
  },
  methods: {
    contactUpdated(contact) {
      this.appointment.contact = contact;
    },
    cancel(event) {
      this.showEditAppointmentForm = false;
    },
    update() {
      this.updating = true;
      const requestBody = {
        startDate: this.appointment.startDate,
        endDate: this.appointment.endDate,
      };

      // TODO: allow unsetting end date (null)

      this.axios
        .patch(
          `/v1/accounts/${this.accountId}/companies/${this.companyId}/appointments/${this.appointment.id}`,
          requestBody
        )
        .then((response) => {
          this.updating = false;

          this.$notify({
            message: "Appointment updated",
            timeout: 3000,
            icon: "fa fa-check",
            type: "success",
          });

          this.$emit("appointment-updated", response.data);
          this.showEditAppointmentForm = false;
        })
        .catch((error) => {
          this.updating = false;
          this.errorMessage = "Failed to update appointment";
          if (get(error, "response.data.message")) {
            this.errorMessage = error.response.data.message;
          }
        });
    },
    deleteAppointment() {
      swal
        .fire({
          title: "Are you sure?",
          text: "This appointment will be deleted",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "gray",
          confirmButtonText: "Yes, delete!",
          reverseButtons: true,
          showLoaderOnConfirm: true,
          preConfirm: (login) => {
            return this.axios
              .delete(
                `/v1/accounts/${this.accountId}/companies/${this.companyId}/appointments/${this.appointment.id}`
              )
              .then((response) => {
                return response;
              })
              .catch((error) => {
                swal.showValidationMessage(
                  `Request failed: ${error.response.data.message}`
                );
              });
          },
          allowOutsideClick: () => !swal.isLoading(),
        })
        .then((result) => {
          if (result.value) {
            swal.fire("Done!", "The appointment was removed", "success");
            // emit event so we can reload data in parent
            this.$emit("appointment-deleted", this.appointment);
          }
        });
    },
  },
};
</script>
